<template>
    <div
        :class="{
            'desktop-layout-form-view': $q.platform.is.desktop
        }"
    >
        <q-form @submit="inEditMode ? update() : create()">
            <q-select
                v-model="productCategory"
                :options="productCategoryOptions"
                :label="$t('Product Category')"
                use-input
                clearable
                outlined
                behavior="menu"
                option-value="name"
                option-label="name"
                class="q-mb-lg"
                color="accent"
                bg-color="white"
            >
                <template v-slot:prepend>
                    <q-icon name="fas fa-tag" />
                </template>
            </q-select>
            <q-input
                v-model="productProperty.name"
                @clear="productProperty.name = null"
                :label="$t('Property Name')"
                class="q-mb-sm q-pb-md"
                style="width: 100%"
                clearable
                outlined
                lazy-rules
                bg-color="white"
                color="accent"
            >
                <template v-slot:prepend>
                    <q-icon name="fas fa-list" />
                </template>
            </q-input>
            <div
                v-for="(property, index) in productProperty.allowed_values"
                :key="index"
            >
                <div
                    class="q-pl-sm"
                    style="display: flex; justify-content: flex-start"
                >
                    <q-separator vertical />
                    <q-input
                        v-model="property.value"
                        @clear="removeAllowedValue(index)"
                        :label="$t(`Allowed Option`) + ` ${index + 1}`"
                        class="q-ml-lg"
                        :class="
                            index === productProperty.allowed_values.length - 1
                                ? 'q-mb-none'
                                : 'q-mb-md'
                        "
                        style="width: 100%"
                        clearable
                        outlined
                        lazy-rules
                        bg-color="white"
                        color="accent"
                    >
                    </q-input>
                </div>
            </div>
            <div
                class="row q-pb-lg q-mt-md"
                style="padding-top: 0; display: flex; justify-content: flex-end"
            >
                <q-btn
                    flat
                    color="primary"
                    :label="$t('Add allowed option')"
                    @click="productProperty.allowed_values.push({ value: '' })"
                />
            </div>
            <!-- action group -->
            <div class="row form__actions justify-between">
                <div class="q-col">
                    <q-btn
                        v-show="inEditMode"
                        @click="handleRemove()"
                        class="float-left q-px-xs q-mb-lg"
                        icon="fas fa-trash-alt"
                        color="negative"
                        outline
                    />
                    <q-space />
                </div>

                <div class="q-col">
                    <q-btn
                        @click="$router.go(-1)"
                        :label="$t('Cancel')"
                        class="q-mr-sm q-mb-lg bg-white"
                        text-color="primary"
                        outline
                    />
                    <q-btn
                        :label="$t('Save')"
                        class="q-mb-lg"
                        type="submit"
                        color="primary"
                        id="save-button"
                    />
                </div>
            </div>
        </q-form>
    </div>
</template>
<script>
import { mapActions, mapState } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useProductsStore } from '@stores/products.js';
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';

export default {
    name: 'ProductPropertyDetail',
    mixins: [BreadcrumbsManager],
    created() {
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.$t('Global Settings'),
                route: {
                    name: 'settings'
                }
            },
            {
                label: this.$t('Product Categories'),
                route: {
                    name: 'product_categories'
                }
            },
            {
                label: this.inEditMode
                    ? this.$t('{property_name} (Product Property)', {
                          property_name: history.state.name
                      })
                    : this.$t('Create product property')
            }
        ]);

        if (this.inEditMode) {
            this.productProperty.name = history.state.name;
            this.productProperty.id = history.state.id;
            this.productProperty.product_category =
                history.state.productCategory;
            this.productProperty.allowed_values = JSON.parse(
                history.state.allowedValues
            );
        }
    },
    computed: {
        ...mapState(useProductsStore, [
            'getProductCategories',
            'getProductCategory'
        ]),
        /**
         * Get the product category options
         * @returns {Array} - Array of product categories
         */
        productCategoryOptions() {
            return this.getProductCategories;
        },
        /**
         * Check if the user is in edit mode
         * @returns {boolean} - true if the user is in edit mode and false otherwise
         */
        inEditMode() {
            return history.state.name !== undefined;
        },
        /**
         * Get the product category name
         * Set the product category id
         * @param {integer} productCategory
         * @returns {string}
         */
        productCategory: {
            get() {
                if (this.productProperty.product_category === null) {
                    return '';
                }
                const category = this.getProductCategory(
                    this.productProperty.product_category
                );
                return category ? category.name : '';
            },
            set(value) {
                this.productProperty.product_category = value.id;
            }
        }
    },
    data() {
        return {
            productProperty: {
                id: null,
                product_category: null,
                name: null,
                allowed_values: []
            }
        };
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useProductsStore, [
            'createProductPropertyAction',
            'updateProductPropertyAction',
            'removeProductPropertyAction'
        ]),
        /**
         * Create a new product property
         */
        async create() {
            // filter out empty allowed values
            this.productProperty.allowed_values =
                this.productProperty.allowed_values.filter(
                    value => value.value !== ''
                );
            // begin loading
            this.beginLoading(`${this.$t('Creating new product property')}...`);
            try {
                await this.createProductPropertyAction(this.productProperty);
                this.$router.go(-1);
                this.$notifySuccess(
                    this.$t(
                        'The product property has been created successfully.'
                    )
                );
            } catch (error) {
                this.handleApiErrors(error, 'create');
            } finally {
                // end loading
                this.stopLoading();
            }
        },
        /**
         * Update a product property
         */
        async update() {
            // filter out empty allowed values
            this.productProperty.allowed_values =
                this.productProperty.allowed_values.filter(
                    value => value.value !== ''
                );
            // begin loading
            this.beginLoading(`${this.$t('Updating product property')}...`);
            try {
                await this.updateProductPropertyAction(this.productProperty);
                this.$router.go(-1);
                this.$notifySuccess(
                    this.$t(
                        'The product property has been updated successfully'
                    )
                );
            } catch (error) {
                this.handleApiErrors(error, 'update');
            } finally {
                // end loading
                this.stopLoading();
            }
        },
        /**
         * Remove a product property
         */
        async handleRemove() {
            try {
                // begin loading
                this.beginLoading(`${this.$t('Removing product property')}...`);
                await this.removeProductPropertyAction(this.productProperty);
                this.$router.go(-1);
                this.$notifySuccess(
                    this.$t(
                        'The product property has been removed successfully.'
                    )
                );
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'Failed to remove product property. Please try again later.'
                    ),
                    error
                );
            } finally {
                // end loading
                this.stopLoading();
            }
        },
        /**
         * Remove allowed value from the list of selected allowed values
         * @param {integer} index
         */
        removeAllowedValue(index) {
            this.productProperty.allowed_values.splice(index, 1);
        },
        /**
         * Handles API errors by performing the specified action.
         * @param {Object} e - The error object returned from the API.
         * @param {String} action - The action that was being performed when the error occurred.
         */
        handleApiErrors(e, action) {
            const error =
                e?.response?.data && typeof e.response.data === 'object'
                    ? e.response.data
                    : {};
            if (
                'error_code' in error &&
                Array.isArray(error['error_code']) &&
                error['error_code'].includes(
                    'product_property_name_already_exists'
                )
            ) {
                this.$notifyError(
                    this.$t(
                        'Product property with this name already exists. Please use a different name.'
                    ),
                    error
                );
            } else if (
                'error_code' in error &&
                Array.isArray(error['error_code']) &&
                error['error_code'].includes(
                    'product_property_allowed_values_not_unique'
                )
            ) {
                this.$notifyError(
                    this.$t(
                        'Ensure that all allowed options are unique when creating a product property.'
                    ),
                    error
                );
            } else {
                if (action === 'create') {
                    this.$notifyError(
                        this.$t(
                            'Failed to create product property. Please try again later.'
                        ),
                        error
                    );
                } else {
                    this.$notifyError(
                        this.$t(
                            'Failed to update product property. Please try again later.'
                        ),
                        error
                    );
                }
            }
        }
    }
};
</script>
